import React, { useRef, useEffect, useState, ChangeEvent } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import axios, { AxiosResponse } from 'axios';

import Layout from '../../components/Layout/AdminLayout/AdminLayout';


import type { LoanStatusDetails, ResponseLoanStatusDetails, LoanAmortization, LoanTermAmortizationPaymentInput } from '../../interface/ILoanManagement';

import helpers from '../../helpers/index';

import { Response } from '../../interface/IResponse';

import loanManagementService from './loanManagementService';

import SuccessModal from '../../components/Modal/SuccessModal';
import ConfirmModal from '../../components/Modal/ConfirmModal';

import LoanAmortizationTable from '../../components/Table/LoanManagement/LoanAmortizationTable';
import LoanAmortizationPaymentModal from './LoanAmortizationPaymentModal';

import confirmIcon from '../../assets/warning-icon.png';

function LoanStatusDetailsPage() {

    var dispatch = useDispatch();
    var navigate = useNavigate();

    var [loanStatusDetails, setLoanStatusDetails] = useState<LoanStatusDetails | null>(null);
    var [loanAmortizations, setLoanAmortizations] = useState<LoanAmortization[]>([]);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [responseMessage, setResponseMessage] = useState("");
    const { loanId } = useParams<{ loanId: string }>();
    const [isModalOpen, setModalOpen] = useState(false);
    const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
    const [loanTermAmortizationPaymentInput, setLoanTermAmortizationPaymentInput] = useState<LoanTermAmortizationPaymentInput>({});

    function getPermission() {

        var permission = helpers.getPermission();
        setIsReadOnly(permission == "ReadOnly");
    }

    async function getMemberDetails() {
        try {

            var response: AxiosResponse<ResponseLoanStatusDetails> = await loanManagementService.getLoanStatusDetails(Number(loanId));

            setLoanStatusDetails(response.data.result || null);

        }
        catch (err) {

            console.log('err:: ', err);

            throw err;
        }

    }

    function formatDate(date: Date | string | undefined): string {
        if (!date) return '';

        let dateObj: Date;

        if (typeof date === 'string') {
            // Truncate the microseconds if present
            date = date.split('.')[0];
            dateObj = new Date(date);

            // Check if the date is valid
            if (isNaN(dateObj.getTime())) {
                return ''; // Invalid date
            }
        } else {
            dateObj = date;
        }

        const monthNames = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];

        return `${monthNames[dateObj.getMonth()]} ${dateObj.getDate()}, ${dateObj.getFullYear()}`;
    }

    async function updatePaymentStatus(loanAmortizationId: number, isPaid: boolean) {

        setLoanTermAmortizationPaymentInput({
            loanAmortizationId: loanAmortizationId,
            isPaid: isPaid
        });
        openConfirmModal();
    }

    function openConfirmModal() {

        setResponseMessage('Are you sure you want to continue? ');
        setConfirmModalOpen(true);

    }


    async function ConfirmPaymentUpdateButtonClick() {
        try {

            document.querySelector('.loader-container')?.classList.add('loader');
            setConfirmModalOpen(false);
            var input: LoanTermAmortizationPaymentInput = loanTermAmortizationPaymentInput;

            var response: AxiosResponse<Response, any> = await loanManagementService.loanTermAmortizationPayment(input);
            var result: Response = response.data;
            await getMemberDetails();

            setResponseMessage("Payment updated successfully");
            setModalOpen(true);
        }
        catch (error) {
            console.log('ConfirmAdminDeleteButtonClick:: ', error);
        }
        finally {

            document.querySelector('.loader-container')?.classList.remove('loader');
        }
    }

    function onCloseConfirmModal() {
        setConfirmModalOpen(false);
        setModalOpen(false);
    }



    const onClose = () => {
        setModalOpen(false);
    };

    useEffect(function () {
        (async () => {

            document.querySelector('.loader-container')?.classList.add('loader');

            await getMemberDetails();
            getPermission();

            document.querySelector('.loader-container')?.classList.remove('loader');
        })();
    }, []);

    return (
        <Layout>
            <ConfirmModal icon={confirmIcon} message={responseMessage} buttonYesText="Yes" isOpen={isConfirmModalOpen} onYes={ConfirmPaymentUpdateButtonClick} onNo={onCloseConfirmModal} />
            <SuccessModal message={responseMessage} isOpen={isModalOpen} onClose={onClose} />
            <div className="title-container">
                <div>
                    <div className="dv-title">
                        <h1>Loan Status</h1>
                    </div>
                  <div>
                        <div className='label-2'>Loan Status:</div>
                        <div className='label-2 font-bold' id='lblLoanStatus'>
                           {
                              (() => {
                                switch(loanStatusDetails?.loanStatus) {
                                  case 0:
                                    return 'Ongoing';
                                  case 1:
                                    return 'Fully Paid';
                                  case 2:
                                    return 'Pending';
                                  default:
                                    return 'Declined';
                                }
                              })()
                            }
                        </div>
                    </div>
                </div>

               <div className="title-btn-container vertical-flex-bottom">
                    <div className="title-btn-container vertical-flex-bottom">
                        <button
                            type="button"
                            id="btnPreviewLoanApplicationForm"
                            onClick={() => {
                                navigate(`/Admin/PreviewLoanApplicationForm/${loanId}`);
                            }}
                            className="btn-white-green-2 center-middle"
                        >
                            Preview Loan Application Form
                        </button>
                    </div>
                     <button
                        type="button"
                        id="btnBack"
                        onClick={function(){
                            navigate(-1);
                        }}
                        className="btn-white-green-2 center-middle"
                    >
                        Back
                    </button>
                </div>
            </div>
            <br />
            <div className='content-container'>
                <div className='padding-container'>
                    <div>
                        <div className='label-2'>Name:</div>
                        <div className='label-2 font-bold' id='lblMemberName'>
                            {loanStatusDetails?.firstname} {loanStatusDetails?.lastname}
                        </div>
                        <div className='label-2'>Loan Amount:</div>
                        <div className='label-2 font-bold' id='lblLoanAmount'>
                            {helpers.formatNumber(loanStatusDetails?.amountLoan ?? 0)}
                        </div>
                    </div>
                    <div>
                        <div className='label-2'>ID:</div>
                        <div className='label-2 font-bold' id='lblMembershipId'>
                            {loanStatusDetails?.membershipId}
                        </div>
                        <div className='label-2'>Terms:</div>
                        <div className='label-2 font-bold' id='lblTerm'>
                            {loanStatusDetails?.term} Months
                        </div>
                    </div>
                    <div>
                        <div className='label-2'>Email:</div>
                        <div className='label-2 font-bold' id='lblEmail'>
                            {loanStatusDetails?.email}
                        </div>
                        <div className='label-2'>Mode of Payment:</div>
                        <div className='label-2 font-bold' id='lblModeOfPayment'>
                            Monthly
                        </div>
                    </div>
                    <div>

                        <div className='label-2'>Amount Subscribed:</div>
                        <div className='label-2 font-bold' id='lblAmountSubscribed'>
                            {helpers.formatNumber(loanStatusDetails?.amountSubscribe ?? 0)}
                        </div>
                        <div className='label-2'>Interest Rate:</div>
                        <div className='label-2 font-bold' id='lblInterestRate'>
                            {loanStatusDetails?.interestRate}%
                        </div>
                    </div>
                    <div>
                        <div className='label-2'>Initial Paid Up:</div>
                        <div className='label-2 font-bold' id='lblInitialPaidUp'>
                            {helpers.formatNumber(loanStatusDetails?.initialPaidUp ?? 0)}
                        </div>
                        <div className='label-2'>Amortization Schedule:</div>
                        <div className='label-2 font-bold' id='lblAmortizationSchedule'>
                            {helpers.formatNumber(loanStatusDetails?.amortizationSchedule ?? 0)}
                        </div>
                    </div>
                    <div>
                        {/* <div className='label-2'>Date Released:</div>
                        <div className='label-2 font-bold' id='lblDateReleased'>
                            {formatDate(loanStatusDetails?.dateReleased)}
                        </div> */}

                         {
                            (() => {
                                if(loanStatusDetails?.loanStatus === 0 || loanStatusDetails?.loanStatus === 1){
                                    return (
                                        <>
                                            <div className='label-2'>Date Released:</div>
                                            <div className='label-2 font-bold' id='lblDateReleased'>
                                                {helpers.formatLongDate(loanStatusDetails?.dateReleased)}
                                            </div>
                                        </>
                                    );
                                }
                                else {
                                    return (
                                       ""
                                    );

                                }
                          })()
                        }
                        <div className='label-2'>Service Fee:</div>
                        <div className='label-2 font-bold' id='lblServiceFee'>
                            {helpers.formatNumber(loanStatusDetails?.serviceFee ?? 0)}
                        </div>
                    </div>
                </div>
            </div>
            <br />

            <div className='content-container'>
                <div className='padding-container'>
                    <LoanAmortizationTable data={loanStatusDetails?.loanAmortizations ?? []} updatePaymentStatus={updatePaymentStatus} ></LoanAmortizationTable>
                </div>
            </div>
        </Layout>
    );
}


export default LoanStatusDetailsPage;